import Link from 'next/link';
import { cn } from '@/lib/utils';

/**
 * Reusable Button component — Trenchmate brand (orange #FF5C00).
 *
 * Variants: primary | secondary | accent | outline | ghost | inverted | ghost-inverted | danger
 * Sizes: default | sm | lg | icon
 *
 * Renders <button> by default, <Link> when href is provided.
 */
export function Button({
    className,
    variant = 'primary',
    size = 'default',
    href,
    children,
    ...props
}) {
    const Component = href ? Link : 'button';

    const base =
        'appearance-none cursor-pointer inline-flex items-center justify-center whitespace-nowrap text-sm font-medium transition-colors duration-300 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-400 disabled:pointer-events-none disabled:opacity-50 tracking-wide rounded-none uppercase border active:scale-[0.97]';

    const variants = {
        primary:
            'bg-brand text-white border-brand hover:bg-white hover:text-brand-dark',
        secondary:
            'bg-white text-brand border-brand hover:bg-brand hover:text-white',
        accent: 'bg-brand-dark text-white border-brand-dark hover:bg-brand hover:border-brand hover:text-white',
        outline:
            'bg-transparent text-brand border-brand hover:bg-brand hover:text-white',
        ghost: 'bg-transparent text-brand border-transparent hover:bg-slate-100',
        inverted:
            'bg-[#fff1e6] text-brand-dark border-[#ffd2b3] font-semibold shadow-[0_10px_24px_rgba(68,20,0,0.16)] hover:bg-white hover:text-[#5a2100] hover:border-[#ffc39a]',
        'ghost-inverted':
            'bg-white/6 text-white border-white/55 font-semibold hover:bg-white/12 hover:border-white',
        danger: 'bg-red-600 text-white border-red-600 hover:bg-red-700 hover:border-red-700',
    };

    const sizes = {
        default: 'h-12 px-8 py-2',
        sm: 'h-10 px-4 text-xs',
        lg: 'h-14 px-10 text-base',
        icon: 'h-10 w-10',
    };

    return (
        <Component
            href={href}
            className={cn(base, variants[variant], sizes[size], className)}
            {...props}
        >
            {children}
        </Component>
    );
}

export default Button;
